import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  MapPin, Handshake, Users, Clock, Phone, Building2, IndianRupee,
  RefreshCw, CheckCircle, ArrowRight, User, Filter, Bell
} from "lucide-react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { useAuth, useProject, API } from "../App";
import { toast } from "sonner";

const SCORE_STYLES = {
  hot: "bg-red-100 text-red-700",
  warm: "bg-orange-100 text-orange-700",
  nurture: "bg-blue-100 text-blue-700",
  dormant: "bg-slate-100 text-slate-500",
};

const formatBudget = (min, max) => {
  const format = (val) => {
    if (!val) return null;
    if (val >= 10000000) return `${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000) return `${(val / 100000).toFixed(0)}L`;
    return `${val}`;
  };
  const minStr = format(min);
  const maxStr = format(max);
  if (minStr && maxStr) return `₹${minStr} - ₹${maxStr}`;
  if (minStr) return `₹${minStr}+`;
  if (maxStr) return `Up to ₹${maxStr}`;
  return "N/A";
};

export default function QueuesPage() {
  const { user } = useAuth();
  const { selectedProject } = useProject();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState("site_visit");
  const [siteVisitQueue, setSiteVisitQueue] = useState([]);
  const [negotiationQueue, setNegotiationQueue] = useState([]);
  const [myClaims, setMyClaims] = useState({ site_visit_claims: [], negotiation_claims: [] });
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState({});

  // Role checks
  const canSeeSiteVisit = ["sales_rep", "sales_executive", "sales_head", "hod_sales", "admin", "ceo"].includes(user?.role);
  const canSeeNegotiation = ["sales_manager", "sales_head", "hod_sales", "admin", "ceo"].includes(user?.role);

  useEffect(() => {
    fetchQueues();
  }, [selectedProject]);

  const fetchQueues = async () => {
    setLoading(true);
    try {
      const projectParam = selectedProject ? `?project_id=${selectedProject}` : "";
      
      const promises = [axios.get(`${API}/queues/my-claims`)];
      
      if (canSeeSiteVisit) {
        promises.push(axios.get(`${API}/queues/site-visit${projectParam}`));
      }
      if (canSeeNegotiation) {
        promises.push(axios.get(`${API}/queues/negotiation${projectParam}`));
      }
      
      const responses = await Promise.all(promises);
      
      setMyClaims(responses[0].data);
      
      let idx = 1;
      if (canSeeSiteVisit && responses[idx]) {
        setSiteVisitQueue(responses[idx].data.leads || []);
        idx++;
      }
      if (canSeeNegotiation && responses[idx]) {
        setNegotiationQueue(responses[idx].data.leads || []);
      }
    } catch (error) {
      console.error("Failed to fetch queues:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async (leadId, type) => {
    setClaiming({ ...claiming, [leadId]: true });
    try {
      await axios.post(`${API}/queues/claim/${leadId}`, { type });
      toast.success("Lead claimed successfully!");
      // For negotiation claims, navigate to lead detail to fill negotiation form
      if (type === "negotiation") {
        navigate(`/leads/${leadId}?open_negotiation=true`);
        return;
      }
      fetchQueues();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim lead");
    } finally {
      setClaiming({ ...claiming, [leadId]: false });
    }
  };

  const handleRelease = async (leadId, type) => {
    try {
      await axios.post(`${API}/queues/release/${leadId}`, { type });
      toast.success("Lead released back to queue");
      fetchQueues();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to release lead");
    }
  };

  const LeadCard = ({ lead, queueType, isClaimed = false }) => (
    <Card 
      className="border-slate-200 hover:border-slate-300 transition-colors cursor-pointer"
      onClick={() => navigate(`/leads/${lead.lead_id}`)}
      data-testid={`queue-lead-${lead.lead_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900">{lead.name}</h3>
              {lead.score && (
                <Badge className={`text-xs ${SCORE_STYLES[lead.score] || "bg-slate-100"}`}>
                  {lead.score}
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Phone className="w-3.5 h-3.5" /> {lead.phone}
              </span>
              {lead.project_interest && (
                <span className="flex items-center gap-1">
                  <Building2 className="w-3.5 h-3.5" /> {lead.project_interest}
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <IndianRupee className="w-3 h-3" /> {formatBudget(lead.budget_min, lead.budget_max)}
              </span>
              {lead.configuration && (
                <span>{lead.configuration}</span>
              )}
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" /> Owner: {lead.owner_name || lead.assigned_to_name || "Unassigned"}
              </span>
            </div>
            
            {queueType === "negotiation" && lead.site_visit_handler_name && (
              <p className="text-xs text-emerald-600 mt-1">
                Site Visit by: {lead.site_visit_handler_name}
              </p>
            )}
          </div>
          
          <div className="ml-4" onClick={(e) => e.stopPropagation()}>
            {isClaimed ? (
              <Button
                size="sm"
                variant="outline"
                className="text-xs text-slate-500 border-slate-200"
                onClick={() => handleRelease(lead.lead_id, queueType)}
              >
                Release
              </Button>
            ) : (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-xs"
                onClick={() => handleClaim(lead.lead_id, queueType)}
                disabled={claiming[lead.lead_id]}
                data-testid={`claim-btn-${lead.lead_id}`}
              >
                {claiming[lead.lead_id] ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>Claim <ArrowRight className="w-3.5 h-3.5 ml-1" /></>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (!canSeeSiteVisit && !canSeeNegotiation) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Card className="bg-white border-slate-200 max-w-md">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Access Restricted</h2>
              <p className="text-slate-600">Lead queues are available for Sales roles only.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="queues-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
              <Users className="w-7 h-7 text-emerald-500" />
              Lead Queues
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Claim leads to handle site visits and negotiations
            </p>
          </div>
          <Button onClick={fetchQueues} variant="outline" className="border-slate-200">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* My Claims Summary */}
        {(myClaims.site_visit_claims.length > 0 || myClaims.negotiation_claims.length > 0) && (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-emerald-800 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> My Active Claims
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex gap-6 text-sm">
                {myClaims.site_visit_claims.length > 0 && (
                  <span className="text-emerald-700">
                    <strong>{myClaims.site_visit_claims.length}</strong> Site Visits
                  </span>
                )}
                {myClaims.negotiation_claims.length > 0 && (
                  <span className="text-emerald-700">
                    <strong>{myClaims.negotiation_claims.length}</strong> Negotiations
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading queues...</div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-slate-100">
              {canSeeSiteVisit && (
                <TabsTrigger value="site_visit" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Site Visits
                  {siteVisitQueue.length > 0 && (
                    <Badge className="ml-1 bg-blue-500 text-white text-xs">{siteVisitQueue.length}</Badge>
                  )}
                </TabsTrigger>
              )}
              {canSeeNegotiation && (
                <TabsTrigger value="negotiation" className="flex items-center gap-2">
                  <Handshake className="w-4 h-4" />
                  Negotiations
                  {negotiationQueue.length > 0 && (
                    <Badge className="ml-1 bg-purple-500 text-white text-xs">{negotiationQueue.length}</Badge>
                  )}
                </TabsTrigger>
              )}
              <TabsTrigger value="my_claims" className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                My Claims
                {myClaims.total > 0 && (
                  <Badge className="ml-1 bg-emerald-500 text-white text-xs">{myClaims.total}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Site Visit Queue */}
            {canSeeSiteVisit && (
              <TabsContent value="site_visit" className="mt-4">
                <div className="space-y-3">
                  {siteVisitQueue.length === 0 ? (
                    <Card className="border-slate-200">
                      <CardContent className="p-8 text-center">
                        <MapPin className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-500">No leads waiting for site visit</p>
                        <p className="text-xs text-slate-400 mt-1">
                          Leads will appear here when scheduled for site visit
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    siteVisitQueue.map(lead => (
                      <LeadCard key={lead.lead_id} lead={lead} queueType="site_visit" />
                    ))
                  )}
                </div>
              </TabsContent>
            )}

            {/* Negotiation Queue */}
            {canSeeNegotiation && (
              <TabsContent value="negotiation" className="mt-4">
                <div className="space-y-3">
                  {negotiationQueue.length === 0 ? (
                    <Card className="border-slate-200">
                      <CardContent className="p-8 text-center">
                        <Handshake className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-500">No leads waiting for negotiation</p>
                        <p className="text-xs text-slate-400 mt-1">
                          Leads will appear here when scheduled for negotiation
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    negotiationQueue.map(lead => (
                      <LeadCard key={lead.lead_id} lead={lead} queueType="negotiation" />
                    ))
                  )}
                </div>
              </TabsContent>
            )}

            {/* My Claims */}
            <TabsContent value="my_claims" className="mt-4 space-y-6">
              {/* Site Visit Claims */}
              {myClaims.site_visit_claims.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-500" /> My Site Visit Claims
                  </h3>
                  <div className="space-y-3">
                    {myClaims.site_visit_claims.map(lead => (
                      <LeadCard key={lead.lead_id} lead={lead} queueType="site_visit" isClaimed />
                    ))}
                  </div>
                </div>
              )}

              {/* Negotiation Claims */}
              {myClaims.negotiation_claims.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Handshake className="w-4 h-4 text-purple-500" /> My Negotiation Claims
                  </h3>
                  <div className="space-y-3">
                    {myClaims.negotiation_claims.map(lead => (
                      <LeadCard key={lead.lead_id} lead={lead} queueType="negotiation" isClaimed />
                    ))}
                  </div>
                </div>
              )}

              {myClaims.site_visit_claims.length === 0 && myClaims.negotiation_claims.length === 0 && (
                <Card className="border-slate-200">
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">You haven't claimed any leads yet</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Go to Site Visits or Negotiations tab to claim leads
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </Layout>
  );
}
